import { MappedEntity } from '@api';
import { getMarketHours } from '@terminal/components/price-display-view/helpers/price-display-helpers';
import { useSnakeMeta } from '@terminal/react-query-hooks/snakes/useSnake';
import { useSubscription } from '@terminal/stores/use-subscription/useSubscription';
import { useScreenerEntities } from '@terminal/widgets/scenario/hooks/use-screener-entities/useScreenerEntities';
import { mapEntityTagsToDefaultOptions } from '@terminal/widgets/scenario/hooks/use-screener-entities-tags/useScreenerEntitiesTags';
import { useEffect } from 'react';
import { useStoreWithEqualityFn } from 'zustand/traditional';

import {
  bindEquityFnWithEntities,
  getLivePriceDataKey,
  Identifier,
  useLivePriceStore,
} from '../use-live-price';
import {
  calculateReturn,
  getIsLivePriceSupported,
  mapToPriceEntities,
  parseData,
} from './utils';

export const useOneDayReturnLivePrice = (
  asset?: MappedEntity,
  identifier?: Identifier
) => {
  const isBasicUser = useSubscription(state => state.isBasicUser());
  const { data: snakeData, isLoading: isSnakeMetaLoading } = useSnakeMeta(
    asset?.default_snake ?? ''
  );
  const { data: entityScreenerData, isFetching: isEntityScreenerFetching } =
    useScreenerEntities({
      filterOptions: [],
      defaultOptionsMapped: mapEntityTagsToDefaultOptions(
        asset ? [asset.tag] : []
      ),
    });

  const isLivePriceSupported = getIsLivePriceSupported(asset);
  const previousClosePrice = snakeData?.last_value;
  const priceEntities = mapToPriceEntities({
    asset,
    isLivePriceSupported,
    lastKnownPrice: previousClosePrice,
  });
  const isNonEquityEntity = asset?.exchange.code === '';

  const { subscribe, livePriceData } = useStoreWithEqualityFn(
    useLivePriceStore,
    s => ({
      subscribe: s.subscribe,
      livePriceData: s.livePriceData,
    }),
    bindEquityFnWithEntities(priceEntities)
  );
  const livePriceDataKey = asset ? getLivePriceDataKey(asset) : undefined;
  const livePrice = livePriceDataKey
    ? livePriceData[livePriceDataKey]
    : undefined;

  useEffect(() => {
    if (!priceEntities?.length || isSnakeMetaLoading) {
      return undefined;
    }

    return subscribe({
      priceEntities,
      delayedPrices: isBasicUser,
      identifier,
    });
  }, [
    priceEntities
      .map(e => e.entity.subscribable_ticker)
      .sort()
      .join(' '),
    isSnakeMetaLoading,
  ]);

  const { isPreMarket, isAfterMarket } = getMarketHours();
  const isExtendedHours = isPreMarket || isAfterMarket;

  const {
    livePriceInfo: { price: livePriceValue, time: livePriceTime },
    snakePriceInfo: { price: snakePriceValue, time: snakePriceTime },
    entityScreenerPriceInfo: {
      price: entityScreenerValue,
      time: entityScreenerTime,
      calculated1dReturn: entityScreenerCalculated1dReturn,
      previousClose,
    },
  } = parseData({
    livePriceData: livePrice,
    snakePriceData: snakeData,
    entityScreenerPriceData: entityScreenerData,
  });

  if (isLivePriceSupported) {
    const isNonEquityEntitySupported = !!livePriceValue && isNonEquityEntity;

    // Use live price:
    //   - during market hours for equities
    //   - during extended hours (pre-market/after-hours) for equities
    //   - anytime, for non-equity entities
    if (!isNonEquityEntity || isNonEquityEntitySupported) {
      const hasLivePrice = !!(livePriceValue && livePriceTime);
      const priceAndDate = hasLivePrice
        ? { price: livePriceValue, date: livePriceTime }
        : { price: entityScreenerValue, date: entityScreenerTime };

      // During extended hours, use snakeData.last_value as baseline:
      //   - Pre-market: last_value = previous day's close
      //   - After-hours: last_value = today's 4pm close
      const baselinePrice = isExtendedHours
        ? previousClosePrice
        : previousClose?.value;

      return {
        ...priceAndDate,
        isLive: !!livePrice?.isLive,
        isSubscribing: hasLivePrice
          ? false
          : isEntityScreenerFetching && !entityScreenerValue,
        oneDayLiveReturn:
          (hasLivePrice && calculateReturn(livePriceValue, baselinePrice)) ||
          entityScreenerCalculated1dReturn,
        isLivePriceSupported,
        isExtendedHours,
      };
    }
  }

  const hasEntityScreenerData = !!(
    entityScreenerValue &&
    entityScreenerTime &&
    entityScreenerCalculated1dReturn
  );

  const priceAndDate = hasEntityScreenerData
    ? { price: entityScreenerValue, date: entityScreenerTime }
    : { price: snakePriceValue, date: snakePriceTime };

  return {
    ...priceAndDate,
    isLive: false,
    isSubscribing: hasEntityScreenerData
      ? !entityScreenerValue
      : snakePriceValue === undefined,
    oneDayLiveReturn: entityScreenerCalculated1dReturn,
    isLivePriceSupported,
    isExtendedHours,
  };
};
